import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((res) => setStats(res.data))
      .catch(() => setStats({ projects: { total: 0, published: 0, drafts: 0 }, leads: { total: 0, new: 0 }, recent_leads: [] }));
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) +
        " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso.slice(0, 16).replace("T", " ");
    }
  };

  return (
    <div className="p-10 max-w-[1180px]" data-testid="admin-dashboard">
      <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-3">Dashboard</div>
      <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em] mb-10">
        Welcome back.
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-testid="dashboard-stats">
        <StatCard
          label="Projects · total"
          value={stats?.projects?.total ?? "—"}
          to="/admin/projects"
          testid="stat-projects-total"
        />
        <StatCard
          label="Published"
          value={stats?.projects?.published ?? "—"}
          to="/admin/projects"
          testid="stat-projects-published"
        />
        <StatCard
          label="Drafts"
          value={stats?.projects?.drafts ?? "—"}
          to="/admin/projects"
          testid="stat-projects-drafts"
        />
        <StatCard
          label="New leads"
          value={stats?.leads?.new ?? "—"}
          to="/admin/leads"
          testid="stat-leads-new"
          highlight={!!stats?.leads?.new}
        />
      </div>

      <section className="bg-white border border-[#e3e3e1] mb-10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e3e3e1]">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#5d5f64]">Recent leads</div>
            <div className="text-sm text-[#16181c] font-medium mt-1">5 most recent inquiries</div>
          </div>
          <Link
            to="/admin/leads"
            className="text-sm text-[#16181c] hover:text-[#5d5f64] inline-flex items-center gap-1"
            data-testid="dashboard-view-all-leads"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {stats?.recent_leads?.length ? (
          <ul data-testid="dashboard-recent-leads">
            {stats.recent_leads.map((l) => (
              <li
                key={l.id}
                className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#e3e3e1] last:border-b-0"
                data-testid={`dashboard-recent-lead-${l.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-[#16181c] truncate">{l.name}</div>
                    {l.status === "new" && (
                      <span className="text-[10px] uppercase tracking-wider bg-[#16181c] text-white px-2 py-0.5">
                        new
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#5d5f64] mt-1 truncate">
                    {l.email} {l.project_type && `· ${l.project_type}`}
                  </div>
                </div>
                <div className="text-xs text-[#5d5f64] whitespace-nowrap pt-1">
                  {fmtDate(l.created_at)}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-6 py-10 text-center text-sm text-[#5d5f64]">
            No leads yet. Submissions from the public site will appear here.
          </div>
        )}
      </section>

      <div className="bg-white border border-[#e3e3e1]">
        <DashLink
          to="/admin/projects"
          title="Manage projects"
          desc="Add, edit, reorder, or remove portfolio entries."
          testid="dashboard-link-projects"
        />
        <DashLink
          to="/admin/categories"
          title="Project categories"
          desc="Add, rename, reorder, or remove portfolio categories."
          testid="dashboard-link-categories"
        />
        <DashLink
          to="/admin/leads"
          title="Inbox · Pesan masuk"
          desc="Read, reply to, and export contact-form leads."
          testid="dashboard-link-leads"
        />
        <DashLink
          to="/admin/content"
          title="Edit homepage content"
          desc="Hero, statement, services, who-we-are, expertise, CTA, footer."
          testid="dashboard-link-content"
        />
        <DashLink
          to="/admin/logo"
          title="Brand & contact"
          desc="Logo, wordmark, and contact details (address, email, phone, WhatsApp, LinkedIn)."
          testid="dashboard-link-logo"
          last
        />
      </div>
    </div>
  );
}

function StatCard({ label, value, to, testid, highlight }) {
  return (
    <Link
      to={to}
      className={`block p-6 border border-[#e3e3e1] hover:border-[#16181c] transition-colors ${
        highlight ? "bg-[#16181c] text-white" : "bg-white text-[#16181c]"
      }`}
      data-testid={testid}
    >
      <div className={`text-xs uppercase tracking-wider mb-2 ${highlight ? "text-white/70" : "text-[#5d5f64]"}`}>
        {label}
      </div>
      <div className="font-display text-4xl font-bold tracking-[-0.02em]">{value}</div>
    </Link>
  );
}

function DashLink({ to, title, desc, testid, last }) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between p-6 hover:bg-[#f3f3f0] ${
        last ? "" : "border-b border-[#e3e3e1]"
      }`}
      data-testid={testid}
    >
      <div>
        <div className="font-medium text-[#16181c]">{title}</div>
        <div className="text-sm text-[#5d5f64]">{desc}</div>
      </div>
      <ArrowRight size={16} />
    </Link>
  );
}
